import * as React from 'react'
import {cva, type VariantProps} from 'class-variance-authority'

import {cn} from '@/lib/utils'

const alertVariants = cva('relative px-3 py-5 mb-4 border border-transparent rounded-[0.1875rem]', {
    variants: {
        variant: {
            default: 'bg-background text-foreground',
            destructive: 'border-[#f1c8d2] bg-[#f5d8df] text-[#6b1f31]'
        }
    },
    defaultVariants: {
        variant: 'default'
    }
})

interface AlertProps
    extends React.HTMLAttributes<HTMLDivElement>,
        VariantProps<typeof alertVariants> {
    'data-testid'?: string
}

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
    ({className, variant, 'data-testid': dataTestId, ...props}, ref) => (
        <div
            ref={ref}
            role="alert"
            className={cn(alertVariants({variant}), className)}
            data-testid={dataTestId || 'Alert'}
            {...props}
        />
    )
)
Alert.displayName = 'Alert'

interface AlertTitleProps extends React.HTMLAttributes<HTMLHeadingElement> {
    'data-testid'?: string
}

const AlertTitle = React.forwardRef<HTMLParagraphElement, AlertTitleProps>(
    ({className, children, 'data-testid': dataTestId, ...props}, ref) => (
        <h5
            ref={ref}
            className={cn('mb-1 font-medium leading-none tracking-tight', className)}
            data-testid={dataTestId || 'Alert-Title'}
            {...props}
        >
            {children}
        </h5>
    )
)
AlertTitle.displayName = 'AlertTitle'

interface AlertDescriptionProps extends React.HTMLAttributes<HTMLParagraphElement> {
    'data-testid'?: string
}

const AlertDescription = React.forwardRef<HTMLParagraphElement, AlertDescriptionProps>(
    ({className, children, 'data-testid': dataTestId, ...props}, ref) => (
        <div
            ref={ref}
            className={cn('body-md lg:text-sm lg:leading-normal', className)}
            data-testid={dataTestId || 'Alert-Description'}
            {...props}
        >
            {children}
        </div>
    )
)
AlertDescription.displayName = 'AlertDescription'

export {Alert, AlertTitle, AlertDescription}
